import { Box, Button, Stack, Switch, Typography } from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import type { SchemeKnobValues, SchemeManifest } from "@shared/types/foyer";
import type { TemplateSubSection } from "../PremiseSidebar";
import { tokens } from "../../theme";
import { screenState } from "./screensModel";

interface Props {
  sub: TemplateSubSection;
  manifest: SchemeManifest | undefined;
  value: SchemeKnobValues | undefined;
  onChange: (next: SchemeKnobValues) => void;
}

/**
 * The on/off state of the screen you are currently editing, shown at the top of
 * its own editor.
 *
 * Before this, on/off lived only under Toggles: you could fill in a screen with
 * no hint that it was switched off, and switching one off deleted the tab you
 * were standing on. Now the switch travels with the content, and an off screen
 * says so in place instead of disappearing.
 */
export default function ScreenStateBar({
  sub,
  manifest,
  value,
  onChange,
}: Props) {
  const state = screenState(sub, manifest, value);
  if (!state) return null;
  const setOn = (on: boolean) =>
    onChange({ ...(value ?? {}), [state.knobId]: on });

  if (!state.on) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        data-testid="screen-state-bar"
        sx={{
          px: 2,
          py: 1.25,
          // Same card language as the Information section cards and the Screens
          // board - a tint, not a slab: this is a status, not an error.
          borderRadius: "14px",
          border: "1px solid",
          borderColor: tokens.warning,
          bgcolor: tokens.warningTint,
        }}
      >
        <VisibilityOffIcon fontSize="small" sx={{ color: tokens.warning }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            This screen is off
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Nothing you fill in here reaches the TV until you turn it on.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          color="warning"
          onClick={() => setOn(true)}
          data-testid="screen-turn-on"
        >
          Turn on
        </Button>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      data-testid="screen-state-bar"
      sx={{ px: 0.5 }}
    >
      <Switch
        size="small"
        checked
        onChange={(e) => setOn(e.target.checked)}
        inputProps={{ "aria-label": `Show ${state.label}` }}
      />
      <Typography variant="body2" color="text.secondary">
        {state.placement === "Inside Information"
          ? "On - reached from the Information tile"
          : "On - shown on the home screen"}
      </Typography>
    </Stack>
  );
}
